import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as apiClient from 'api/apiClients';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import ESpinner from '../others/Spinner';
import TitleCounter from '../others/TitleCounter';
import OffClientEdit from './OffClientEdit';
import OffClientInfo from './OffClientInfo';
import ClientToolBar from './ClientToolBar';
import ClientsTable from './ClientsTable';
import { defaultClientsStatus, useClients } from './context/ClientsProvider';

const Clients = () => {
    const { t } = useTranslation(['Clients', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const {
        showSpinner,
        setShowSpinner,
        clientsOrder,
        clientsDesc,
        clientsStatus,
        setClientsOrder,
        setClientsDesc,
        setClientsStatus,
        listResetNonce,
        listRefreshNonce,
    } = useClients();

    const [clientsList, setClientsList] = useState<any[] | null>(null);
    const [page, setPage] = useState<number | null>(null);
    const [editClientId, setEditClientId] = useState<number | null>(null);
    const [infoClientId, setInfoClientId] = useState<number | null>(null);
    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const fetchClients = async (force = false) => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return;

        const listData = {
            search,
            order: `${clientsOrder ? `order by ${clientsOrder} ` : ''}${clientsDesc ? 'DESC' : ''}`,
            page: page < 0 ? 1 : page || 1,
            onPage,
            status: clientsStatus,
        };

        if (prevListData.current === JSON.stringify(listData)) return;
        prevListData.current = JSON.stringify(listData);

        setShowSpinner(true);
        const resClients = await apiClient.getList(listData);
        setShowSpinner(false);

        if (!resClients) return showToast(t('common:messError'), 'danger');

        g.setPaginator((prev: any) => ({
            ...prev,
            [location.pathname]: resClients.paginator,
        }));

        setClientsList(resClients.clients);
    };

    const resetClientsList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        setClientsOrder('');
        setClientsDesc(false);
        setClientsStatus(defaultClientsStatus);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const listData = {
            search,
            order: '',
            page: 1,
            onPage,
            status: defaultClientsStatus,
        };

        setShowSpinner(true);
        const resClients = await apiClient.getList(listData);
        setShowSpinner(false);

        if (!resClients) {
            skipFetchOnStateChangeRef.current = false;
            return showToast(t('common:messError'), 'danger');
        }

        prevListData.current = JSON.stringify(listData);

        g.setPaginator((prev: any) => ({
            ...prev,
            [location.pathname]: resClients.paginator,
        }));

        setClientsList(resClients.clients);
        skipFetchOnStateChangeRef.current = false;
    }, [g, location.pathname, onPage, setClientsDesc, setClientsOrder, setClientsStatus, setShowSpinner, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetClientsList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchClients(true);
    });

    useEffect(() => {
        if (page === 1) fetchClients(true);
        else setPage(1);
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        fetchClients();
    }, [page, clientsStatus, clientsOrder, clientsDesc, onPage]);

    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== _page) setPage(_page);
    }, [g?.paginator?.[location.pathname]?.currentPage, g?.paginator?.[location.pathname]?.countRows, g?.paginator?.[location.pathname]?.onPage]);

    return (
        <>
            <OffClientEdit clientId={editClientId} setClientId={setEditClientId} fetchClients={fetchClients} />

            <OffClientInfo clientId={infoClientId} setClientId={setInfoClientId} />

            <Row>
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('title')}</h4>
                            <TitleCounter />
                        </div>
                        <div className="mt-2 mt-sm-0">
                            <ClientToolBar setEditClientId={setEditClientId} />
                        </div>
                    </div>
                </Col>

                <Col sm={12}>
                    {clientsList ? (
                        <ClientsTable
                            clientsList={clientsList}
                            fetchClients={fetchClients}
                            setEditClientId={setEditClientId}
                            setInfoClientId={setInfoClientId}
                        />
                    ) : (
                        <ESpinner />
                    )}
                </Col>
            </Row>
        </>
    );
};

export default Clients;
